import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useIsMaster } from '@/hooks/useIsMaster';

export type ViewAsProfile = 
  | 'visitor' 
  | 'subscriber_basic' 
  | 'subscriber_essential' 
  | 'subscriber_enterprise' 
  | 'admin'
  | null;

export interface ImpersonationContextType {
  viewAs: ViewAsProfile;
  setViewAs: (profile: ViewAsProfile) => void;
  isImpersonating: boolean;
  canImpersonate: boolean;
  effectiveProfile: ViewAsProfile;
  resetView: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const STORAGE_KEY = 'ai4me_view_as';

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isMaster, loading: masterLoading } = useIsMaster();
  const [viewAs, setViewAsState] = useState<ViewAsProfile>(null);

  // Carregar do localStorage ao iniciar (apenas para masters)
  useEffect(() => {
    if (masterLoading) return;
    
    if (isMaster) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && ['visitor', 'subscriber_basic', 'subscriber_essential', 'subscriber_enterprise', 'admin'].includes(stored)) {
        setViewAsState(stored as ViewAsProfile);
      }
    } else {
      // Limpar se não for master
      localStorage.removeItem(STORAGE_KEY);
      setViewAsState(null);
    }
  }, [isMaster, masterLoading]);

  const setViewAs = useCallback((profile: ViewAsProfile) => {
    if (!isMaster) return;
    
    setViewAsState(profile);
    if (profile) {
      localStorage.setItem(STORAGE_KEY, profile);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isMaster]);

  const resetView = useCallback(() => {
    setViewAsState(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Limpar ao fazer logout (detectado quando isMaster muda para false)
  useEffect(() => {
    if (!masterLoading && !isMaster && viewAs) {
      resetView();
    }
  }, [isMaster, masterLoading, viewAs, resetView]);

  const value: ImpersonationContextType = {
    viewAs,
    setViewAs,
    isImpersonating: !!viewAs && isMaster,
    canImpersonate: isMaster,
    effectiveProfile: viewAs,
    resetView,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

// Utility para obter o display name do perfil
export const getProfileDisplayName = (profile: ViewAsProfile): string => {
  const names: Record<NonNullable<ViewAsProfile>, string> = {
    visitor: 'Usuário Free',
    subscriber_basic: 'Assinante Básico',
    subscriber_essential: 'Assinante Profissional',
    subscriber_enterprise: 'Assinante Empresarial',
    admin: 'Admin',
  };
  return profile ? names[profile] : 'Superadmin';
};
